import { useCallback } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient, AllergyIntolerance } from '@medplum/fhirtypes';
import { HL7 } from '@/consts/systems.consts';

export interface UseAllergiesReturn {
  /**
   * Load all active allergies for a patient (excludes entered-in-error).
   */
  loadPatientAllergies: (
    patient: Patient
  ) => Promise<{ allergies: AllergyIntolerance[] }>;
}

/**
 * Hook for querying allergy data from the server.
 * Handles all read operations for AllergyIntolerance resources.
 *
 * @returns Object with query functions
 *
 * @example
 * ```tsx
 * const { loadPatientAllergies } = useAllergies();
 *
 * // Load allergies
 * const result = await loadPatientAllergies(patient);
 * ```
 */
export function useAllergies(): UseAllergiesReturn {
  const medplum = useMedplum();

  const loadPatientAllergies = useCallback(
    async (
      patient: Patient
    ): Promise<{ allergies: AllergyIntolerance[] }> => {
      if (!patient?.id) {
        return {
          allergies: [],
        };
      }

      // Load all allergies for the patient, excluding those marked as entered-in-error
      const allAllergies = await medplum.searchResources('AllergyIntolerance', {
        patient: `Patient/${patient.id}`,
        'verification-status:not':
          HL7.TERMINOLOGY.CS.ALLERGY_INTOLERANCE.VERIFICATION.CODES
            .ENTERED_IN_ERROR,
      });

      return {
        allergies: allAllergies,
      };
    },
    [medplum]
  );

  return { loadPatientAllergies };
}

